import { Component, input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LeaveRequestRow } from '../../models/leave.model';
import { formatDateMDY, toISODate } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';

// ── Badge colors matching the image ──────────────────────────────────────────
const LEAVE_TYPE_COLORS: Record<string, { bg: string; dot: string; text: string }> = {
  sick:              { bg: '#FEE9D1', dot: '#F59E0B', text: '#92400E' },
  casual:            { bg: '#F1F5F9', dot: '',        text: '#334155' },
  holiday:           { bg: '#FEF9C3', dot: '#D97706', text: '#78350F' },
  'family emergency':{ bg: '#FEE2E2', dot: '#EF4444', text: '#991B1B' },
  annual:            { bg: '#D1FAE5', dot: '#10B981', text: '#065F46' },
};

function leaveTypeStyle(name: string): { bg: string; dot: string; text: string } {
  const key = (name ?? '').toLowerCase();
  for (const k of Object.keys(LEAVE_TYPE_COLORS)) {
    if (key.includes(k)) return LEAVE_TYPE_COLORS[k];
  }
  return { bg: '#F1F5F9', dot: '', text: '#334155' };
}

function initials(name: string): string {
  return (name ?? '').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
}

const AVATAR_COLORS = ['#2469A0','#7B61FF','#E85D75','#F59E0B','#10B981','#6366F1','#EC4899','#14B8A6','#F97316','#8B5CF6'];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export interface PanelLeaveRow {
  employeeName:  string;
  initials:      string;
  avatarColor:   string;
  /** Array of date strings — one per line in the cell */
  dateLines:     string[];
  leaveType:     string;
  style:         { bg: string; dot: string; text: string };
  isHalfDay:     boolean;
  halfDayTime?:  string;
  totalDays:     number;
  /** Hourly mode only: "09:00 AM - 10:00 AM" */
  timeRange?:    string;
}

@Component({
  selector: 'ers-manager-leave-panel',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, FormsModule],
  templateUrl: './manager-leave-panel.component.html',
  styleUrls: ['./manager-leave-panel.component.scss'],
})
export class ManagerLeavePanelComponent {
  rowData = input<LeaveRequestRow[]>([]);
  onLeaveTitle = input('Employee on Leave');
  upcomingTitle = input('Upcoming Employee Leaves');
  isHourlyMode = input(false);
  isLoading = input(false);

  constructor() {
    effect(() => { this.rowData(); this.recompute(); });
  }

  selectedDate: string = toISODate(new Date());

  onLeaveRows:  PanelLeaveRow[] = [];
  upcomingRows: PanelLeaveRow[] = [];

  get selectedDateDisplay(): string {
    return formatDateMDY(this.selectedDate);
  }

  onDateChange(): void               { this.recompute(); }
  resetToToday(): void {
    this.selectedDate = toISODate(new Date());
    this.recompute();
  }

  private isApprovedStatus(status: string | null | undefined): boolean {
    return (status ?? '').trim().toLowerCase() === 'approved';
  }

  private recompute(): void {
    const sel   = new Date(this.selectedDate + 'T00:00:00');
    const today = new Date(new Date().toDateString());

    const approved = this.rowData().filter(
      r => this.isApprovedStatus(r.status) && r.startDateRaw && r.endDateRaw,
    );

    this.onLeaveRows = approved
      .filter(r => {
        const s = new Date(r.startDateRaw + 'T00:00:00');
        const e = new Date(r.endDateRaw   + 'T00:00:00');
        return sel >= s && sel <= e;
      })
      .map(r => this.toRow(r));

    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

    this.upcomingRows = approved
      .filter(r => {
        const start = new Date(r.startDateRaw + 'T00:00:00');
        return start > today && start <= sevenDaysFromNow;
      })
      .sort((a, b) => a.startDateRaw.localeCompare(b.startDateRaw))
      .map(r => this.toRow(r));
  }

  private toRow(r: LeaveRequestRow): PanelLeaveRow {
    const fmt      = (iso: string) => formatDateMDY(iso);
    const isSame   = r.startDateRaw === r.endDateRaw;
    const isHalfDay = r.totalDays > 0 && r.totalDays < 1;

    // Multi-day: each date on its own line; single-day or half-day: one line
    let dateLines: string[];
    if (isHalfDay) {
      dateLines = [`${fmt(r.startDateRaw)}(Half Day)`];
    } else if (isSame) {
      dateLines = [fmt(r.startDateRaw)];
    } else {
      dateLines = [fmt(r.startDateRaw), fmt(r.endDateRaw)];
    }

    return {
      employeeName: r.employeeName,
      initials:     initials(r.employeeName),
      avatarColor:  avatarColor(r.employeeName),
      dateLines,
      leaveType:    r.leaveType,
      style:        leaveTypeStyle(r.leaveType),
      isHalfDay,
      halfDayTime:  isHalfDay ? `${r.from} to ${r.to}` : undefined,
      totalDays:    r.totalDays ?? 0,
      timeRange:    this.isHourlyMode() ? `${r.from} - ${r.to}` : undefined,
    };
  }
}
